import React, { useState } from "react";
import {
	Box,
	Typography,
	Menu,
	MenuItem,
	Divider,
	Link,
	IconButton,
} from "@mui/material";
import {
	ChevronDown,
	LayoutDashboard,
	GraduationCap,
	Bell,
	MessagesSquare,
	Calendar,
	Upload,
	UserCircle,
	LogOut,
	Home,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { ROLES } from "../../../config/supabase";
import { TEACHER_PAGE_BG } from "./teacherModuleStyles";

const menuLinkSx = {
	color: "primary.main",
	fontSize: "0.875rem",
	fontWeight: 500,
	minHeight: "auto",
	py: 0.75,
	px: 1.5,
	"&:hover": {
		backgroundColor: "transparent",
		textDecoration: "underline",
	},
	"&.Mui-focusVisible": {
		backgroundColor: "action.hover",
	},
};

const TeacherModuleChrome = ({ children }) => {
	const { user, supabase } = useAuth();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const teacherCanAccessContent =
		user?.profile?.role === ROLES.SCHOOL_TEACHER &&
		user?.profile?.status === "Active";

	const displayName =
		user?.profile?.full_name?.trim() ||
		user?.email?.split("@")[0] ||
		"Teacher";

	const handleNav = (path) => {
		setAnchorEl(null);
		navigate(path);
	};

	const handleLogout = async () => {
		setAnchorEl(null);
		try {
			await supabase.auth.signOut();
		} catch (e) {
			console.error(e);
		}
		try {
			const keys = Object.keys(localStorage);
			keys.forEach((key) => {
				if (key.startsWith("sb-") && key.includes("auth-token")) {
					localStorage.removeItem(key);
				}
			});
		} catch (e) {
			console.error(e);
		}
		navigate("/login", { replace: true });
	};

	return (
		<Box sx={{ ...TEACHER_PAGE_BG, pb: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>
			<Box
				sx={{
					maxWidth: 960,
					mx: "auto",
					width: "100%",
					mb: 2,
					pt: { xs: 2, sm: 2.5 },
					pb: 1.25,
					display: "flex",
					alignItems: "center",
					justifyContent: { xs: "space-between", sm: "center" },
					gap: { xs: 1, sm: 1.75 },
					flexWrap: "nowrap",
					backgroundColor: "transparent",
				}}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: { xs: 0.25, sm: 0.75 },
						minWidth: 0,
						maxWidth: { xs: "calc(100% - 120px)", sm: "none" },
					}}>
					<IconButton
						aria-label="Go to dashboard"
						size="small"
						onClick={() => navigate("/dashboard")}
						sx={{
							color: "text.primary",
							flexShrink: 0,
							"&:hover": { backgroundColor: "action.hover" },
						}}>
						<Home size={22} strokeWidth={2.25} />
					</IconButton>
					<Typography
						component="span"
						sx={{
							color: "text.primary",
							fontWeight: 700,
							fontSize: { xs: "1.15rem", sm: "1.3rem" },
							letterSpacing: "0.01em",
							lineHeight: 1.3,
							minWidth: 0,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}>
						Hello {displayName}
					</Typography>
				</Box>

				<Divider
					orientation="vertical"
					flexItem
					sx={{
						display: { xs: "none", sm: "block" },
						borderColor: "divider",
						alignSelf: "stretch",
						my: 0.25,
					}}
				/>

				<Box sx={{ flexShrink: 0 }}>
					<Link
						component="button"
						type="button"
						id="teacher-nav-menu-button"
						aria-controls={open ? "teacher-nav-menu" : undefined}
						aria-haspopup="true"
						aria-expanded={open ? "true" : undefined}
						onClick={(e) => setAnchorEl(e.currentTarget)}
						underline="none"
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: 0.5,
							cursor: "pointer",
							fontWeight: 700,
							fontSize: { xs: "1.3rem", sm: "1.2rem" },
							color: "primary.main",
							border: "none",
							background: "none",
							padding: 0,
							fontFamily: "inherit",
							textDecoration: "none",
							minHeight: { xs: 44, sm: 40 },
							px: { xs: 0.5, sm: 0 },
							py: { xs: 0.5, sm: 0 },
							"&:hover": {
								textDecoration: "none",
								color: "primary.dark",
							},
							"&:focus-visible": {
								outline: "2px solid",
								outlineColor: "primary.main",
								outlineOffset: 2,
								borderRadius: 0.5,
							},
						}}>
						Menu
						<ChevronDown size={24} strokeWidth={2.5} />
					</Link>

					<Menu
						id="teacher-nav-menu"
						anchorEl={anchorEl}
						open={open}
						onClose={() => setAnchorEl(null)}
						MenuListProps={{
							"aria-labelledby": "teacher-nav-menu-button",
							dense: true,
						}}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
						slotProps={{
							paper: {
								sx: {
									mt: 1,
									minWidth: 220,
									borderRadius: 1,
									boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
									border: "1px solid",
									borderColor: "divider",
								},
							},
						}}>
						<MenuItem onClick={() => handleNav("/dashboard")} sx={menuLinkSx}>
							<LayoutDashboard
								size={16}
								style={{ marginRight: 10, opacity: 0.85 }}
							/>
							Funmath Games
						</MenuItem>
						{teacherCanAccessContent && (
							<MenuItem
								onClick={() => handleNav("/teacher/onboarding")}
								sx={menuLinkSx}>
								<GraduationCap
									size={16}
									style={{ marginRight: 10, opacity: 0.85 }}
								/>
								Onboarding videos
							</MenuItem>
						)}
						{teacherCanAccessContent && (
							<MenuItem
								onClick={() => handleNav("/my-notifications")}
								sx={menuLinkSx}>
								<Bell size={16} style={{ marginRight: 10, opacity: 0.85 }} />
								My Notifications
							</MenuItem>
						)}
						<MenuItem onClick={() => handleNav("/feedback")} sx={menuLinkSx}>
							<MessagesSquare
								size={16}
								style={{ marginRight: 10, opacity: 0.85 }}
							/>
							Feedback
						</MenuItem>
						{teacherCanAccessContent && (
							<MenuItem onClick={() => handleNav("/schedule")} sx={menuLinkSx}>
								<Calendar size={16} style={{ marginRight: 10, opacity: 0.85 }} />
								Timetable
							</MenuItem>
						)}
						{teacherCanAccessContent && (
							<MenuItem
								onClick={() => handleNav("/game-results-upload")}
								sx={menuLinkSx}>
								<Upload size={16} style={{ marginRight: 10, opacity: 0.85 }} />
								Uploading
							</MenuItem>
						)}
						<MenuItem onClick={() => handleNav("/account")} sx={menuLinkSx}>
							<UserCircle size={16} style={{ marginRight: 10, opacity: 0.85 }} />
							Account
						</MenuItem>
						<Divider sx={{ my: 0.5 }} />
						<MenuItem onClick={handleLogout} sx={menuLinkSx}>
							<LogOut size={16} style={{ marginRight: 10, opacity: 0.85 }} />
							Logout
						</MenuItem>
					</Menu>
				</Box>
			</Box>
			<Box sx={{ maxWidth: 960, mx: "auto", width: "100%" }}>{children}</Box>
		</Box>
	);
};

export default TeacherModuleChrome;
